import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScrollAnimation } from '../hooks/ScrollAnimation';
import { useTheme } from '../../context/ThemeContext';
import {
  FaGamepad, FaFilm, FaMusic, FaBook, FaCamera, FaPalette,
  FaExternalLinkAlt, FaArrowRight, FaPen
} from 'react-icons/fa';

const HobbiesSection = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [tooltip, setTooltip] = useState({ visible: false, text: "", x: 0, y: 0 });
  const { ref: hobbiesRef, isVisible: hobbiesVisible } = useScrollAnimation({ threshold: 0.1, repeat: true });

  const hobbies = [
    { icon: <FaGamepad className="text-lg" />, name: "Gaming",      color: "bg-red-100 text-red-600",       link: "/gaming",                        tooltip: "Lihat koleksi game steam saya"                          },
    { icon: <FaPalette className="text-lg" />, name: "Drawing",     color: "bg-blue-100 text-blue-600",     link: "/draw-gallery",                  tooltip: "Jelajahi galeri lukisan saya"                           },
    { icon: <FaMusic   className="text-lg" />, name: "Music",       color: "bg-yellow-100 text-yellow-600", link: "/music",                         tooltip: "Dengarkan genre music yang saya sukai"                  },
    { icon: <FaBook    className="text-lg" />, name: "Reading",     color: "bg-green-100 text-green-600",   link: "https://thenightgardenlibrary.vercel.app/",                   tooltip: "Lihat buku-buku yang mengubah cara saya melihat dunia" },
    { icon: <FaCamera  className="text-lg" />, name: "Photography", color: "bg-purple-100 text-purple-600", link: "https://khalens.vercel.app/",                   tooltip: "Lihat koleksi foto saya"                                },
    { icon: <FaFilm    className="text-lg" />, name: "Film",        color: "bg-stone-100 text-stone-600",   link: "/movie-review",                  tooltip: "Lihat koleksi film saya"                                },
    { icon: <FaPen     className="text-lg" />, name: "Blog",        color: "bg-yellow-400 text-slate-600",  link: "https://nakhalanblog.vercel.app", tooltip: "Baca tulisan & blog saya"                               },
  ];

  const darkHobbies = [
    { color: "bg-red-900/40 text-red-400" },
    { color: "bg-blue-900/40 text-blue-400" },
    { color: "bg-yellow-900/40 text-yellow-400" },
    { color: "bg-green-900/40 text-green-400" },
    { color: "bg-purple-900/40 text-purple-400" },
    { color: "bg-stone-800/40 text-stone-400" },
    { color: "bg-yellow-900/40 text-yellow-400" },
  ];

  const containerBg = isDark ? 'bg-[#111C2C]' : 'bg-blue-50';
  const titleColor = isDark ? 'text-blue-300' : 'text-blue-800';

  const handleMouseMove = (e, text) => setTooltip({ visible: true, text, x: e.clientX + 15, y: e.clientY + 15 });
  const handleMouseLeave = () => setTooltip({ visible: false, text: "", x: 0, y: 0 });
  const handleHobbyClick = (link) => link.startsWith("http") ? window.open(link, "_blank", "noopener,noreferrer") : navigate(link);

  return (
    <>
      <div
        ref={hobbiesRef}
        className={`${containerBg} pixel-border p-4 w-full max-w-xs mb-4`}
        style={{
          opacity: hobbiesVisible ? 1 : 0,
          transform: hobbiesVisible ? 'translateX(0)' : 'translateX(-24px)',
          transition: 'opacity 0.6s ease-out 0.05s, transform 0.6s ease-out 0.05s',
        }}
      >
        <h4 className={`text-sm font-semibold ${titleColor} mb-1 pixel-font text-center`}>HOBBIES & INTERESTS</h4>
        <div className="grid grid-cols-2 gap-3">
          {hobbies.map((hobby, index) => {
            const isExternal = hobby.link.startsWith("http");
            const colorClass = isDark ? darkHobbies[index].color : hobby.color;
            return (
              <div
                key={index}
                role="button"
                tabIndex={0}
                aria-label={hobby.tooltip}
                className={`relative flex flex-col items-center justify-center gap-1 p-3 pixel-border ${colorClass} cursor-pointer
                  shadow-[3px_3px_0_0_rgba(15,23,42,0.85)]
                  hover:shadow-[1px_1px_0_0_rgba(15,23,42,0.85)]
                  hover:translate-x-[2px] hover:translate-y-[2px]
                  active:shadow-none active:translate-x-[3px] active:translate-y-[3px]
                  focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2
                  transition-all duration-150 group`}
                onMouseMove={(e) => handleMouseMove(e, hobby.tooltip)}
                onMouseLeave={handleMouseLeave}
                onClick={() => handleHobbyClick(hobby.link)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleHobbyClick(hobby.link);
                  }
                }}
              >
                <div className="relative">
                  {hobby.icon}
                  {isExternal ? (
                    <FaExternalLinkAlt className="absolute -top-1.5 -right-3.5 text-[9px] opacity-70 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-150" />
                  ) : (
                    <FaArrowRight className="absolute -top-1 -right-3.5 text-[9px] opacity-70 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all duration-150" />
                  )}
                </div>
                <span className="text-xs font-medium">{hobby.name}</span>
                <span className="text-[9px] font-semibold uppercase tracking-wide opacity-60 group-hover:opacity-100 transition-opacity duration-150">
                  Lihat →
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {tooltip.visible && (
        <div
          className={`fixed text-white text-xs px-2 py-1 rounded pixel-border z-50 pointer-events-none ${isDark ? 'bg-[#111C2C]' : 'bg-gray-800'}`}
          style={{ left: `${tooltip.x}px`, top: `${tooltip.y}px` }}
        >
          {tooltip.text}
        </div>
      )}
    </>
  );
};

export default HobbiesSection;
